import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '../../ui/components';
import {
  Send, Paperclip, CheckCircle, AlertTriangle, TrendingUp,
  Clock, ArrowLeft, Circle,
} from 'lucide-react';
import { setCurrentStep } from '../../store/loanApplicationSlice';

const HumanUnderwritingFlow = () => {
  const dispatch = useDispatch();
  const { user, recommendedProduct } = useSelector((state) => state.loanApplication);
  
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      sender: 'underwriter',
      name: 'Sarah Mitchell',
      message: `Hi ${user?.firstName || 'there'}! I'm Sarah, your dedicated underwriter. I've reviewed your application and it looks great! I have a few questions to help strengthen your file.`,
      timestamp: '10:30 AM',
      avatar: '/avatars/sarah.jpg',
    },
    {
      id: 2,
      sender: 'underwriter',
      name: 'Sarah Mitchell',
      message: 'Can you provide your most recent pay stub? This will help verify your income.',
      timestamp: '10:31 AM',
      avatar: '/avatars/sarah.jpg',
    },
    {
      id: 3,
      sender: 'user',
      name: user?.firstName || 'You',
      message: 'Sure! I will upload that right now.',
      timestamp: '10:35 AM',
    },
  ]);

  // Mock underwriter data
  const underwriter = {
    name: 'Sarah Mitchell',
    title: 'Senior Mortgage Underwriter',
    experience: '12 years',
    certifications: ['NMLS #123456', 'Certified Underwriter'],
    specialty: 'Conventional & FHA Loans',
    availability: 'Online',
    responseTime: 'Typically responds in 15 min',
    avatar: '/avatars/sarah.jpg',
  };

  // Mock loan analysis
  const loanAnalysis = {
    overallScore: 82,
    strengths: [
      { label: 'Excellent Credit Score', value: 760, impact: 'high', description: 'Well above minimum requirements' },
      { label: 'Strong Income', value: '$95,000/year', impact: 'high', description: 'Stable W-2 employment' },
      { label: 'Low Debt-to-Income', value: '28%', impact: 'high', description: 'Well below 43% threshold' },
      { label: 'Sufficient Down Payment', value: '20%', impact: 'medium', description: 'Avoids PMI requirement' },
    ],
    weaknesses: [
      { label: 'Limited Credit History', severity: 'low', description: 'Only 3 years of credit history', recommendation: 'Not a major concern for this loan type' },
      { label: 'Recent Job Change', severity: 'low', description: 'Started new position 8 months ago', recommendation: 'Provide offer letter and first pay stub' },
    ],
    opportunities: [
      { label: 'Rate Improvement', description: 'Your credit score qualifies you for our best rates' },
      { label: 'Faster Closing', description: 'All major items look good - could close in 10 days' },
    ],
  };

  // Mock conditions
  const [conditions, setConditions] = useState([
    {
      id: 1,
      title: 'Recent Pay Stub',
      description: 'Most recent pay stub dated within 30 days',
      status: 'pending',
      priority: 'high',
      dueDate: '2 days',
      category: 'Income Verification',
    },
    {
      id: 2,
      title: 'Bank Statements',
      description: 'Last 2 months of checking account statements',
      status: 'in_review',
      priority: 'high',
      dueDate: '3 days',
      category: 'Asset Verification',
    },
    {
      id: 3,
      title: 'Homeowners Insurance Quote',
      description: 'Quote from licensed insurance provider',
      status: 'completed',
      priority: 'medium',
      dueDate: 'Completed',
      category: 'Property',
    },
    {
      id: 4,
      title: 'Employment Verification',
      description: 'Verbal VOE from current employer',
      status: 'completed',
      priority: 'high',
      dueDate: 'Completed',
      category: 'Income Verification',
    },
    {
      id: 5,
      title: 'Appraisal Review',
      description: 'Property appraisal to confirm value',
      status: 'not_started',
      priority: 'medium',
      dueDate: '5 days',
      category: 'Property',
    },
  ]);

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;

    const newMessage = {
      id: chatMessages.length + 1,
      sender: 'user',
      name: user?.firstName || 'You',
      message: chatMessage,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    };

    setChatMessages([...chatMessages, newMessage]);
    setChatMessage('');

    // Simulate underwriter response
    setTimeout(() => {
      const response = {
        id: chatMessages.length + 2,
        sender: 'underwriter',
        name: 'Sarah Mitchell',
        message: 'Thanks for that! Let me review and I will get back to you shortly.',
        timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        avatar: '/avatars/sarah.jpg',
      };
      setChatMessages(prev => [...prev, response]);
    }, 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'var(--lk-success-500)';
      case 'in_review': return 'var(--lk-primary-500)';
      case 'pending': return 'var(--lk-warning-500)';
      default: return 'var(--lk-text-quaternary)';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'completed': return 'var(--lk-success-50)';
      case 'in_review': return 'var(--lk-primary-50)';
      case 'pending': return 'var(--lk-warning-50)';
      default: return 'var(--lk-bg-surface-2)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={20} />;
      case 'in_review': return <Clock size={20} />;
      case 'pending': return <Circle size={20} />;
      default: return <Circle size={20} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_review': return 'In Review';
      case 'pending': return 'Action Needed';
      default: return 'Not Started';
    }
  };

  const completedCount = conditions.filter(c => c.status === 'completed').length;
  const totalCount = conditions.length;
  const progressPercent = (completedCount / totalCount) * 100;

  // Shared card style
  const cardStyle = {
    backgroundColor: 'var(--lk-bg-surface-1)', borderRadius: 'var(--lk-radius-xl)',
    border: '1px solid var(--lk-border)', padding: 'var(--lk-space-6)',
  };

  // Info row helper
  const infoRow = (label, value) => (
    <div>
      <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{label}</div>
      <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 500 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'var(--lk-space-6)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => dispatch(setCurrentStep(3))} style={{ marginBottom: 'var(--lk-space-3)' }}>
          Back to Underwriting Selection
        </Button>
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>Working with Your Underwriter</h2>
        <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-base)' }}>
          Sarah is reviewing your application and will guide you through the final steps
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--lk-space-6)', alignItems: 'start' }}>
        {/* Left Column - Underwriter Profile & Chat */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-5)' }}>
          {/* Underwriter Profile */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-3)', marginBottom: 'var(--lk-space-4)' }}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  backgroundColor: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, fontWeight: 700,
                }}>SM</div>
                <div style={{
                  position: 'absolute', bottom: 2, right: 2, width: 12, height: 12,
                  borderRadius: '50%', backgroundColor: 'var(--lk-success-500)',
                  border: '2px solid var(--lk-bg-surface-1)',
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--lk-text-md)', fontWeight: 700 }}>{underwriter.name}</div>
                <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', marginBottom: 6 }}>{underwriter.title}</div>
                <span style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                  backgroundColor: 'var(--lk-success-50)', color: 'var(--lk-success-700)',
                  fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                }}>{underwriter.availability}</span>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-4) 0' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              {infoRow('Experience', underwriter.experience)}
              {infoRow('Specialty', underwriter.specialty)}
              {infoRow('Response Time', underwriter.responseTime)}
            </div>
          </div>

          {/* Chat Interface */}
          <div style={{ ...cardStyle, padding: 0, height: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: 'var(--lk-space-4)', borderBottom: '1px solid var(--lk-border)' }}>
              <div style={{ fontSize: 'var(--lk-text-md)', fontWeight: 700 }}>Chat with Sarah</div>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>Ask questions anytime</div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--lk-space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
                {chatMessages.map((msg) => (
                  <div key={msg.id} style={{ display: 'flex', flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row', gap: 8 }}>
                    {msg.sender === 'underwriter' && (
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        backgroundColor: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, flexShrink: 0,
                      }}>S</div>
                    )}
                    <div style={{
                      maxWidth: '75%', padding: 'var(--lk-space-3)',
                      borderRadius: 'var(--lk-radius-lg)',
                      backgroundColor: msg.sender === 'user' ? 'var(--lk-primary-500)' : 'var(--lk-bg-surface-2)',
                      color: msg.sender === 'user' ? 'white' : 'var(--lk-text-primary)',
                    }}>
                      <div style={{ fontSize: 'var(--lk-text-sm)' }}>{msg.message}</div>
                      <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.7, marginTop: 4 }}>{msg.timestamp}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div style={{ padding: 'var(--lk-space-3)', borderTop: '1px solid var(--lk-border)' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  style={{
                    flex: 1, padding: '8px 12px', fontSize: 'var(--lk-text-sm)',
                    border: '1px solid var(--lk-border)', borderRadius: 'var(--lk-radius-md)',
                    outline: 'none', backgroundColor: 'var(--lk-bg-surface-1)',
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!chatMessage.trim()}
                  style={{
                    width: 36, height: 36, borderRadius: 'var(--lk-radius-md)',
                    backgroundColor: chatMessage.trim() ? 'var(--lk-primary-500)' : 'var(--lk-bg-surface-2)',
                    color: chatMessage.trim() ? 'white' : 'var(--lk-text-quaternary)',
                    border: 'none', cursor: chatMessage.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Loan Analysis & Conditions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-5)' }}>
          {/* Loan Health Score */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--lk-success-50) 0%, var(--lk-primary-50) 100%)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--lk-space-5)', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 56, fontWeight: 700, color: 'var(--lk-success-600)' }}>{loanAnalysis.overallScore}</div>
                <div style={{ fontSize: 'var(--lk-text-md)', fontWeight: 600, marginBottom: 4 }}>Loan Health Score</div>
                <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>Excellent - Ready for approval</div>
              </div>
              <div>
                <p style={{ fontSize: 'var(--lk-text-sm)', marginBottom: 8 }}>
                  <strong>Great news!</strong> Your application is in excellent shape. You have strong financials and meet all major requirements for the {recommendedProduct?.name || 'loan program'}.
                </p>
                <p style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', margin: 0 }}>
                  Sarah has identified a few minor items to complete, but you're on track for a smooth approval process.
                </p>
              </div>
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--lk-space-5)' }}>
            {/* Strengths */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--lk-space-4)' }}>
                <TrendingUp size={20} color="var(--lk-success-500)" />
                <h6 style={{ margin: 0, fontWeight: 700 }}>Your Strengths</h6>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-4)' }}>
                {loanAnalysis.strengths.map((s, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600 }}>{s.label}</span>
                      <span style={{
                        padding: '2px 10px', borderRadius: 12,
                        backgroundColor: 'var(--lk-success-50)', color: 'var(--lk-success-700)',
                        fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                      }}>{s.value}</span>
                    </div>
                    <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>{s.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Minor Items */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--lk-space-4)' }}>
                <AlertTriangle size={20} color="var(--lk-warning-500)" />
                <h6 style={{ margin: 0, fontWeight: 700 }}>Minor Items</h6>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-4)' }}>
                {loanAnalysis.weaknesses.map((w, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600, marginBottom: 4 }}>{w.label}</div>
                    <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)', marginBottom: 4 }}>{w.description}</div>
                    <div style={{ fontSize: 'var(--lk-text-xs)', fontStyle: 'italic', color: 'var(--lk-primary-600)' }}>
                      💡 {w.recommendation}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Conditions Tracker */}
          <div style={cardStyle}>
            <div style={{ marginBottom: 'var(--lk-space-5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lk-space-3)' }}>
                <h6 style={{ margin: 0, fontWeight: 700 }}>Conditions Checklist</h6>
                <span style={{
                  padding: '4px 12px', borderRadius: 12,
                  backgroundColor: 'var(--lk-primary-50)', color: 'var(--lk-primary-600)',
                  fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                }}>{completedCount} of {totalCount} Complete</span>
              </div>
              <div style={{ height: 8, borderRadius: 9999, backgroundColor: 'var(--lk-bg-surface-2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progressPercent}%`, borderRadius: 9999, backgroundColor: 'var(--lk-success-500)', transition: 'width 0.3s ease' }} />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              {conditions.map((c) => (
                <div key={c.id} style={{
                  padding: 'var(--lk-space-4)', borderRadius: 'var(--lk-radius-lg)',
                  backgroundColor: c.status === 'completed' ? 'var(--lk-bg-surface-2)' : 'var(--lk-bg-surface-1)',
                  border: '1px solid var(--lk-border-subtle)',
                }}>
                  <div style={{ display: 'flex', gap: 'var(--lk-space-3)' }}>
                    <div style={{ color: getStatusColor(c.status), paddingTop: 2 }}>
                      {getStatusIcon(c.status)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 'var(--lk-text-base)', fontWeight: 600, marginBottom: 4 }}>{c.title}</div>
                          <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>{c.description}</div>
                        </div>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, flexShrink: 0,
                          backgroundColor: getStatusBg(c.status), color: getStatusColor(c.status),
                          fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                        }}>{getStatusLabel(c.status)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12,
                          border: '1px solid var(--lk-border)', fontSize: 'var(--lk-text-xs)',
                        }}>{c.category}</span>
                        <span style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>Due: {c.dueDate}</span>
                        {c.status === 'pending' && (
                          <Button variant="primary" size="sm" icon={Paperclip} style={{ marginLeft: 'auto' }}>
                            Upload
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HumanUnderwritingFlow;
